import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Reveal, MaskedLines, Chapter } from "../components/Motion";
import { Field, SubmitButton, useSubmission } from "../components/Forms";
import { createCheckout } from "../lib/api";
import { CreditCard, Landmark, HeartHandshake, Package, HandHelping } from "lucide-react";

const PAY_METHODS = [
  { id: "debit", label: "Debit Card", online: true, icon: CreditCard },
  { id: "credit", label: "Credit Card", online: true, icon: CreditCard },
  { id: "ach", label: "ACH", online: false, icon: Landmark },
  { id: "transfer", label: "Bank Transfer", online: false, icon: Landmark },
];

const DONATION_AMOUNTS = [25, 50, 100, 250, 500];

const DESIGNATIONS = [
  { id: "medical", label: "Medical" },
  { id: "education", label: "Education" },
  { id: "relief", label: "Relief" },
  { id: "community", label: "Community" },
  { id: "where_most_needed", label: "Where most needed" },
];

const IN_KIND = [
  "Water filters and hygiene kits",
  "Books, stationery, and uniforms",
  "Blankets and winter clothing",
  "Sewing machines for women's centres",
  "Basic medical consumables",
];

const VOLUNTEER_ROLES = [
  "Doctor", "Nurse", "Teacher", "Linguist / Translator", "Writer",
  "Marketer", "Videographer", "Mechanic", "Construction", "Other",
];

function InvoiceForm() {
  const { sending, sent, send } = useSubmission("invoice_request");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (sent) {
    return (
      <div className="rounded-2xl border border-teal/30 bg-teal/5 p-6 text-center" data-testid="invoice-success">
        <p className="font-serif text-xl text-teal">Invoice request received.</p>
        <p className="mt-2 text-sm text-charcoal-muted">We will reply with an itemised invoice including ACH and bank-transfer details.</p>
      </div>
    );
  }

  return (
    <form
      className="space-y-5"
      data-testid="invoice-form"
      onSubmit={(e) => {
        e.preventDefault();
        send(form);
      }}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" htmlFor="inv-name">
          <input id="inv-name" required className="field" value={form.name} onChange={set("name")} data-testid="invoice-name-input" placeholder="First name is fine" />
        </Field>
        <Field label="Email" htmlFor="inv-email">
          <input id="inv-email" required type="email" className="field" value={form.email} onChange={set("email")} data-testid="invoice-email-input" placeholder="you@company.com" />
        </Field>
      </div>
      <Field label="Service / project" htmlFor="inv-subject">
        <input id="inv-subject" required className="field" value={form.subject} onChange={set("subject")} data-testid="invoice-subject-input" placeholder="e.g. Translation of 40-page report" />
      </Field>
      <Field label="Details" htmlFor="inv-message">
        <textarea id="inv-message" required rows={4} className="field" value={form.message} onChange={set("message")} data-testid="invoice-message-input" placeholder="Scope, volume, deadline, preferred payment method (ACH / bank transfer)…" />
      </Field>
      <SubmitButton sending={sending} testid="invoice-submit-button" className="btn-outline w-full">Request an invoice</SubmitButton>
    </form>
  );
}

function PayPanel() {
  const [method, setMethod] = useState("debit");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const selected = PAY_METHODS.find((m) => m.id === method);

  const pay = async () => {
    const value = Number(amount);
    if (!value || value < 1) {
      toast.error("Enter an amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        kind: "service",
        amount: value,
        method: selected.label,
        note: reference,
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="pay-panel">
      <div>
        <p className="field-label">Payment method</p>
        <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Payment method">
          {PAY_METHODS.map((m) => (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={method === m.id}
              onClick={() => setMethod(m.id)}
              data-testid={`pay-method-${m.id}`}
              className={`flex items-center gap-2.5 rounded-xl border px-4 py-3.5 text-sm font-semibold transition-colors duration-300 ${
                method === m.id
                  ? "border-teal bg-teal text-cream"
                  : "border-line bg-white/70 text-charcoal hover:border-teal/50"
              }`}
            >
              <m.icon size={17} strokeWidth={1.8} aria-hidden="true" />
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {selected.online ? (
        <div className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Amount (USD)" htmlFor="pay-amount">
              <input
                id="pay-amount"
                type="number"
                min="1"
                step="0.01"
                className="field"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="pay-amount-input"
                placeholder="250.00"
              />
            </Field>
            <Field label="Invoice / project reference" htmlFor="pay-ref">
              <input
                id="pay-ref"
                className="field"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                data-testid="pay-reference-input"
                placeholder="Optional"
              />
            </Field>
          </div>
          <button onClick={pay} disabled={loading} className="btn-primary w-full" data-testid="pay-submit-button">
            {loading ? "Opening secure checkout…" : `Pay $${amount || "—"} securely`}
          </button>
          <p className="text-xs leading-relaxed text-charcoal-muted">
            Card payments are processed securely by Stripe. A receipt is emailed to
            you automatically after payment.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-line bg-sand/50 p-6" data-testid="offline-method-note">
          <p className="text-sm leading-relaxed text-charcoal">
            <strong>{selected.label}</strong> payments are handled by invoice so the
            transfer details match your records exactly. Request an invoice below and
            we will reply with itemised {selected.id === "ach" ? "ACH" : "bank-transfer"} instructions.
          </p>
        </div>
      )}

      <div className="border-t border-line pt-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal">Prefer an invoice?</p>
        <p className="mt-2 mb-6 text-sm text-charcoal-muted">
          Tell us the scope and we will send an itemised invoice — ACH and bank transfer included.
        </p>
        <InvoiceForm />
      </div>
    </div>
  );
}

function MiniInterestForm({ formType, roles, testid, buttonLabel, placeholder }) {
  const { sending, sent, send } = useSubmission(formType);
  const [form, setForm] = useState({ name: "", email: "", intent: roles?.[0] || "", message: "" });

  if (sent) {
    return (
      <p className="rounded-xl border border-teal/30 bg-teal/5 p-4 text-sm text-teal" data-testid={`${testid}-success`}>
        Noted — thank you. We will be in touch.
      </p>
    );
  }

  return (
    <form
      className="space-y-4"
      data-testid={`${testid}-form`}
      onSubmit={(e) => {
        e.preventDefault();
        send(form);
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <input required className="field" placeholder="First name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid={`${testid}-name-input`} />
        <input required type="email" className="field" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid={`${testid}-email-input`} />
      </div>
      {roles && (
        <select className="field" value={form.intent} onChange={(e) => setForm((f) => ({ ...f, intent: e.target.value }))} data-testid={`${testid}-role-select`}>
          {roles.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
      )}
      <textarea rows={3} className="field" placeholder={placeholder} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid={`${testid}-message-input`} />
      <SubmitButton sending={sending} testid={`${testid}-submit-button`} className="btn-outline w-full">{buttonLabel}</SubmitButton>
    </form>
  );
}

function DonatePanel() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [designation, setDesignation] = useState("where_most_needed");
  const [loading, setLoading] = useState(false);

  const effective = custom !== "" ? Number(custom) : amount;

  const donate = async () => {
    if (!effective || effective < 1) {
      toast.error("Enter a gift of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        kind: "donation",
        amount: effective,
        designation,
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="donate-panel">
      <div>
        <p className="field-label">Choose an amount (USD)</p>
        <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Donation amount">
          {DONATION_AMOUNTS.map((a) => (
            <button
              key={a}
              type="button"
              role="radio"
              aria-checked={custom === "" && amount === a}
              onClick={() => { setAmount(a); setCustom(""); }}
              data-testid={`donate-amount-${a}`}
              className={`rounded-full px-6 py-3 font-serif text-lg font-semibold transition-colors duration-300 ${
                custom === "" && amount === a
                  ? "bg-teal text-cream"
                  : "border border-line bg-white/70 text-charcoal hover:border-teal/50"
              }`}
            >
              ${a}
            </button>
          ))}
          <input
            type="number"
            min="1"
            step="0.01"
            className="field !w-36 !rounded-full text-center font-semibold"
            placeholder="Custom"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            aria-label="Custom donation amount"
            data-testid="donate-custom-amount"
          />
        </div>
      </div>

      <div>
        <p className="field-label">Designation</p>
        <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label="Donation designation">
          {DESIGNATIONS.map((d) => (
            <button
              key={d.id}
              type="button"
              role="radio"
              aria-checked={designation === d.id}
              onClick={() => setDesignation(d.id)}
              data-testid={`designation-${d.id.replace(/_/g, "-")}`}
              className={`rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                designation === d.id
                  ? "border-teal bg-teal text-cream"
                  : "border-line bg-white/70 text-charcoal hover:border-teal/50"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-sand-deep bg-sand/60 p-5" data-testid="honest-line">
        <p className="text-xs sm:text-sm leading-relaxed text-charcoal">
          <strong>An honest line:</strong> Most of every gift supports programs in India.
          A necessary share also covers coordination hubs, utilities, transport, and basic
          operations. We do not claim that 100% of every donation goes only to field programs.
        </p>
      </div>

      <button onClick={donate} disabled={loading} className="btn-primary w-full !py-4 text-base" data-testid="donate-submit-button">
        <HeartHandshake size={18} aria-hidden="true" />
        {loading ? "Opening secure checkout…" : `Donate $${effective || "—"} — ${DESIGNATIONS.find((d) => d.id === designation)?.label}`}
      </button>
      <p className="text-xs leading-relaxed text-charcoal-muted">
        Processed securely by Stripe. A receipt is emailed to you automatically
        after payment.
      </p>

      <div className="border-t border-line pt-8 space-y-10">
        <div>
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-teal">
            <Package size={15} aria-hidden="true" /> Give in kind
          </p>
          <ul className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {IN_KIND.map((item) => (
              <li key={item} className="flex gap-2.5 text-sm text-charcoal/90">
                <span className="mt-[8px] h-1.5 w-1.5 shrink-0 rotate-45 bg-teal" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <MiniInterestForm
              formType="in_kind"
              testid="inkind"
              buttonLabel="Offer supplies"
              placeholder="What you can offer, rough quantity, and where it ships from…"
            />
          </div>
        </div>
        <div>
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-teal">
            <HandHelping size={15} aria-hidden="true" /> Volunteer
          </p>
          <p className="mt-3 text-sm leading-relaxed text-charcoal-muted">
            Doctors, nurses, teachers, linguists, writers, marketers, videographers,
            mechanics, construction — the work needs hands of every kind.
          </p>
          <div className="mt-5">
            <MiniInterestForm
              formType="volunteer"
              roles={VOLUNTEER_ROLES}
              testid="volunteer"
              buttonLabel="Register interest"
              placeholder="Skills, availability, remote or in person…"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Support() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "pay" ? "pay" : "donate";

  return (
    <>
      <Seo
        title="Support & Pay — Hire ProOne or Fund Rural Work"
        description="Pay for ProOne services by card, ACH, or bank transfer — or support the nonprofit's healthcare, education, relief, and community work in rural India."
        path="/support"
      />

      <section className="pt-28 sm:pt-36 pb-12 sm:pb-16" data-testid="support-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="eyebrow flex items-center gap-3">
            <span className="h-px w-8 bg-teal" aria-hidden="true" />
            Support / Pay
          </p>
          <MaskedLines
            lines={["Two ways to", <span key="t" className="italic text-teal">move this forward.</span>]}
            className="mt-6 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-charcoal"
          />
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-charcoal-muted">
              Pay an invoice for professional work — or give to the nonprofit. Both are
              one click away, and both are handled with the same care.
            </p>
          </Reveal>

          <Reveal delay={0.6}>
            <div className="mt-10 inline-flex rounded-full border border-line bg-white/70 p-1.5" role="tablist" aria-label="Support or pay">
              <button
                role="tab"
                aria-selected={tab === "pay"}
                onClick={() => setParams({ tab: "pay" })}
                data-testid="tab-pay"
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  tab === "pay" ? "bg-teal text-cream" : "text-charcoal hover:text-teal"
                }`}
              >
                Hire / Pay for services
              </button>
              <button
                role="tab"
                aria-selected={tab === "donate"}
                onClick={() => setParams({ tab: "donate" })}
                data-testid="tab-donate"
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  tab === "donate" ? "bg-teal text-cream" : "text-charcoal hover:text-teal"
                }`}
              >
                Support the nonprofit
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28" data-testid="support-panels">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-[2rem] border border-line bg-cream p-6 sm:p-10 lg:p-12">
              <div className="mb-10">
                <Chapter
                  number={tab === "pay" ? "01" : "02"}
                  eyebrow={tab === "pay" ? "Services" : "Nonprofit"}
                  title={tab === "pay" ? "Pay for professional services" : "Support the work in rural India"}
                  className="!mb-0"
                />
              </div>
              <div className="max-w-2xl">{tab === "pay" ? <PayPanel /> : <DonatePanel />}</div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
